import Link from "next/link";
import { cn } from "@plura/shared/utils";

/**
 * Landing redesign Button — pill CTA. Thin wrapper over the global `.btn`
 * classes (see globals.css `@layer components`), so the same styling is usable
 * as plain `class="btn btn-primary"` in raw HTML/CMS.
 *
 * Deliberately standalone (not the shared `@plura/client/components` Button) —
 * the new landing design diverges from the app theme. Width fits content.
 */
const variantClass = {
    /** #121212 bg, white text — main CTA (use on light backgrounds). */
    primary: "btn-primary",
    /** Solid white bg, dark text — primary CTA for dark sections. */
    white: "btn-white",
    /** White bg, 1px dark pill border, dark text — secondary/outline CTA. */
    secondary: "btn-secondary",
    /** Transparent, dark text — e.g. header "Log in". */
    ghost: "btn-ghost",
} as const;

export type ButtonVariant = keyof typeof variantClass;

const baseClass = "btn";

type BaseProps = {
    variant?: ButtonVariant;
    /** default = 56px, medium = 48px, small = 40px. */
    size?: "default" | "medium" | "small";
    className?: string;
    children: React.ReactNode;
};

type ButtonAsButton = BaseProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { href?: undefined };

type ButtonAsLink = BaseProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & { href: string };

export function Button(props: ButtonAsButton | ButtonAsLink) {
    const { variant = "primary", size = "default", className, children, ...rest } = props;
    const sizeClass = { default: "", medium: "btn-md", small: "btn-sm" } as const;
    const classes = cn(baseClass, variantClass[variant], sizeClass[size], className);

    // Render a Next link when `href` is provided, otherwise a native button.
    if (props.href != null) {
        return (
            <Link className={classes} {...(rest as Omit<ButtonAsLink, keyof BaseProps>)}>
                {children}
            </Link>
        );
    }

    return (
        <button className={classes} {...(rest as Omit<ButtonAsButton, keyof BaseProps>)}>
            {children}
        </button>
    );
}
